import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../ui/Modal/Modal';
import { FileUpload } from '../../ui/FileUpload/FileUpload';
import { Input } from '../../ui/Input/Input';
import { RootState } from '../../../store';
import { resetFileState } from '../../../store/slices/fileUploadSlice';
import { useDocumentUpload } from '../../../hooks/useDocumentUpload';
import { submitRequirementEvidence } from '../../../services/RequirementStepsService';
import { supportedFiles } from '../Documentation/Documentation.const';
import {
  dateRequiredMessage,
  futureDateMessage,
  futureDated,
  toAttestationDate,
  todayDateInputValue
} from '../../../utils/evidenceHistory';
import {
  attestationDateLabel,
  attestationDateRequirement,
  evidenceDocumentCategory
} from '../../../utils/evidenceConfig';
import { getApiErrorCode } from '../../../utils/apiError';
import { evidenceSubmitError } from '../../../utils/evidenceErrors';

export type EvidenceUploadTarget = {
  actionId: string;
  description: string;
  evidenceConfig?: Record<string, unknown> | null;
  // Shown before the user commits, because reopening a dependent step DELETES its submitted
  // evidence — and `applyActionInvalidations` fires on EVERY submission, not only renewals.
  //
  // Built by the caller, which names the affected steps: it resolves the action's
  // `invalidatesStepKeys` against the step list, which this modal does not hold. That is why the
  // copy arrives as a prop rather than being derived here. Generic wording is the fallback for one
  // case only — the field absent, i.e. an API predating it. Undefined when nothing is at risk.
  resetWarning?: string;
};

type RequirementEvidenceDocumentModalProps = {
  target: EvidenceUploadTarget | null;
  aiSystemId: string;
  onClose: () => void;
  onSubmitted: () => void;
  // Called when the server rejects because the step is already satisfied — the local view is stale,
  // so the caller resyncs and surfaces the reopen route.
  onAlreadySatisfied: (message: string) => void;
};

/**
 * Evidence document upload, using the platform's standard drag-and-drop FileUpload rather than the
 * bespoke "Choose file" control this screen used to have.
 *
 * Rendered by RequirementListStep as a SIBLING of the SideDrawer, never inside it: Modal is z-10
 * against the drawer backdrop's z-40, and SideDrawer returns null when closed. The caller therefore
 * closes the drawer while this is open and reopens it afterwards, matching the existing FRIA/DPIA
 * and importer upload flows.
 */
export function RequirementEvidenceDocumentModal({
  target,
  aiSystemId,
  onClose,
  onSubmitted,
  onAlreadySatisfied
}: RequirementEvidenceDocumentModalProps) {
  const dispatch = useDispatch();
  const files = useSelector((state: RootState) => state.fileUpload);
  const [date, setDate] = useState('');
  const [dateError, setDateError] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const category = evidenceDocumentCategory(target?.evidenceConfig) ?? 'OTHER';
  const dateRequirement = attestationDateRequirement(target?.evidenceConfig);
  const showDate = dateRequirement !== 'none';
  const dateRequired = dateRequirement === 'required';
  const dateLabel = attestationDateLabel(target?.evidenceConfig);

  // Its own hook instance rather than the shared one in RequirementListStep, whose onSuccess
  // branches across the FRIA/DPIA/conformity/importer flows.
  const { uploadFiles, uploading } = useDocumentUpload({
    aiSystemId,
    category
  });

  const reset = () => {
    setDate('');
    setDateError(undefined);
    setError(undefined);
    setSubmitting(false);
    dispatch(resetFileState());
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const handleUpload = async () => {
    if (!target || submitting) return;

    if (showDate && date && futureDated(date)) {
      setDateError(futureDateMessage(dateLabel));
      return;
    }

    const attestationDate = date ? toAttestationDate(date) : undefined;
    if (dateRequired && !attestationDate) {
      setDateError(dateRequiredMessage(dateLabel));
      return;
    }

    setError(undefined);
    setDateError(undefined);
    setSubmitting(true);
    try {
      const results = await uploadFiles(files, category);
      const failed = results.find((result) => !result.success);
      if (failed) {
        setError(failed.error ?? 'Failed to upload. Please try again.');
        return;
      }
      const documentId = results.find((result) => result.success)?.documentId;
      if (!documentId) {
        throw new Error('Upload did not return a document id');
      }

      await submitRequirementEvidence(
        target.actionId,
        {
          documentId,
          ...(attestationDate && { attestationDate })
        },
        // We render the failure inline below; without this the interceptor would toast the same
        // problem a second time (EAA-1937).
        { skipErrorToast: true }
      );
      reset();
      onSubmitted();
    } catch (submitError) {
      console.error('Failed to submit document evidence:', submitError);
      // Branch on the CODE, never the message: those strings are user-facing copy and are localized
      // server-side (see utils/apiError.ts).
      if (getApiErrorCode(submitError) === 'REQUIREMENT_STEP_ALREADY_SATISFIED') {
        reset();
        onAlreadySatisfied(
          'This obligation is already complete and is not yet due for renewal. Reopen the task below to submit new evidence.'
        );
        return;
      }
      // Routed through the shared mapper so a server-side date rejection lands on the date
      // field, worded exactly as this modal's own client guards word it.
      const { field, message } = evidenceSubmitError(submitError, {
        dateLabel,
        fallback: 'Failed to upload. Please try again.'
      });
      if (field === 'date') setDateError(message);
      else setError(message);
    } finally {
      // In `finally`, not the success branch: this component stays mounted across the parent's
      // refetch, and a success path that skipped it left the button stuck on "Uploading...".
      setSubmitting(false);
    }
  };

  return (
    // No `setOpen`: Modal's Dialog has `onClose={() => {}}`, so it never dismisses itself and never
    // invokes that prop. Cancel comes from FileUpload's own button, which calls handleCancel.
    <Modal open={Boolean(target)} dialogPanelClassnames="w-[820px] overflow-visible">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <p className="text-gray-900 text-lg leading-6 font-medium">
            Upload evidence
          </p>
          {target && (
            <p className="text-sm text-gray-500 text-left">
              {target.description}
            </p>
          )}
        </div>

        {showDate && (
          <Input
            id="evidence-attestation-date"
            type="date"
            label={dateRequired ? `${dateLabel} (required)` : `${dateLabel} (optional)`}
            value={date}
            maxLength={10}
            max={todayDateInputValue()}
            onChange={(e) => {
              setDate(e.target.value);
              if (dateError) setDateError(undefined);
            }}
            labelClassName="label"
            inputClassName="input"
            error={dateError}
          />
        )}

        {target?.resetWarning && (
          <p className="text-sm text-amber-700 text-left">
            {target.resetWarning}
          </p>
        )}

        {error && <span className="text-sm text-red-600 text-left">{error}</span>}

        <FileUpload
          suportedFiles={supportedFiles}
          maxFiles={1}
          minFiles={1}
          uploading={uploading || submitting}
          uploadDisabled={dateRequired && !date}
          handleCancel={handleCancel}
          handleUpload={handleUpload}
          buttonText={uploading || submitting ? 'Uploading...' : 'Upload'}
        />
      </div>
    </Modal>
  );
}
